<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import '@validations';
import {
  BModal, BRow, BCol, BFormGroup, BFormRadioGroup,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import axios from '@/libs/axios';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormRadioGroup,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        comunicadotitulo_ptbr: '',
        comunicadoconteudo_ptbr: '',
        destinatarioTipo: 'TODOS',
        representanteIds: [],
      }),
    },
    titleModal: { type: String, default: null },
    idModal: { type: String, default: null },
    labelBtnOk: { type: String, default: null },
    submitHandler: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      destinatarios: [],
      loadingDestinatarios: false,
      submitting: false,
      hasSubmitted: false,
      recipientOptions: [
        { text: 'Todos os representantes', value: 'TODOS' },
        { text: 'Representantes específicos', value: 'ESPECIFICOS' },
      ],
    };
  },
  computed: {
    dataForm() {
      return this.value;
    },
  },
  mounted() {
    this.onModalShow = (bvEvent, modalId) => {
      if (modalId !== this.idModal) return;
      this.hasSubmitted = false;
      if (!this.dataForm.destinatarioTipo) this.$set(this.dataForm, 'destinatarioTipo', 'TODOS');
      if (!Array.isArray(this.dataForm.representanteIds)) this.$set(this.dataForm, 'representanteIds', []);
      this.fetchDestinatarios();
      this.$nextTick(() => this.$refs.form && this.$refs.form.reset());
    };
    this.$root.$on('bv::modal::show', this.onModalShow);
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::show', this.onModalShow);
  },
  methods: {
    onRecipientTypeChange(type) {
      if (type === 'ESPECIFICOS') this.dataForm.representanteIds = [];
    },
    async fetchDestinatarios() {
      this.loadingDestinatarios = true;
      try {
        const response = await axios.get('/comunicado/destinatarios');
        this.destinatarios = (response.data.data || []).map(rep => ({
          value: String(rep.representanteid),
          text: rep.representantenome || rep.representanteid,
        }));
      } catch (error) {
        this.destinatarios = [];
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: error.response?.data?.message || 'Não foi possível carregar os destinatários.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loadingDestinatarios = false;
      }
    },
    recipientSelectionValid() {
      if (this.dataForm.destinatarioTipo === 'LEGADO_GLOBAL') return true;
      return this.dataForm.destinatarioTipo === 'TODOS'
        ? this.destinatarios.length > 0
        : Array.isArray(this.dataForm.representanteIds) && this.dataForm.representanteIds.length > 0;
    },
    async validateForm() {
      if (this.submitting) return;
      this.hasSubmitted = true;
      const valid = await this.$refs.form.validate();
      if (valid && this.recipientSelectionValid()) {
        this.submitting = true;
        try {
          const saved = await this.submitHandler(this.dataForm);
          if (saved === true) this.$bvModal.hide(this.idModal);
        } finally {
          this.submitting = false;
        }
        return;
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Atenção',
          text: 'Preencha título, conteúdo e selecione destinatários válidos.',
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        },
      });
    },
    fieldState(errors) {
      if (!this.hasSubmitted) return null;
      return errors.length ? false : null;
    },
  },
};
</script>

<template>
  <b-modal
    :id="idModal"
    :title="titleModal"
    :ok-title="labelBtnOk"
    ok-variant="success"
    cancel-title="Cancelar"
    no-close-on-backdrop
    no-close-on-esc
    :ok-disabled="submitting"
    :cancel-disabled="submitting"
    :hide-header-close="submitting"
    @ok.prevent="validateForm"
    @cancel="$emit('cancel')"
    @close="$emit('cancel')"
  >
    <validation-observer ref="form">
      <b-row>
        <b-col cols="12">
          <b-form-group label="Título" label-for="comunicado-titulo">
            <validation-provider v-slot="{ errors }" name="Título" rules="required">
              <b-form-input
                id="comunicado-titulo"
                v-model="dataForm.comunicadotitulo_ptbr"
                :state="fieldState(errors)"
                maxlength="255"
                placeholder="Digite o título"
              />
              <small class="text-danger">{{ hasSubmitted ? errors[0] : '' }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group label="Conteúdo" label-for="comunicado-conteudo">
            <validation-provider v-slot="{ errors }" name="Conteúdo" rules="required">
              <b-form-textarea
                id="comunicado-conteudo"
                v-model="dataForm.comunicadoconteudo_ptbr"
                :state="fieldState(errors)"
                rows="5"
                maxlength="255"
                placeholder="Digite o conteúdo"
              />
              <small class="text-danger">{{ hasSubmitted ? errors[0] : '' }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group label="Destinatários">
            <p v-if="dataForm.destinatarioTipo === 'LEGADO_GLOBAL'" class="mb-0 text-muted">
              Todos os usuários (comunicado legado)
            </p>
            <b-form-radio-group
              v-else
              v-model="dataForm.destinatarioTipo"
              :options="recipientOptions"
              stacked
              @change="onRecipientTypeChange"
            />
          </b-form-group>
          <b-form-group
            v-if="dataForm.destinatarioTipo === 'ESPECIFICOS'"
            label="Selecione os representantes"
          >
            <v-select
              v-model="dataForm.representanteIds"
              :options="destinatarios"
              :loading="loadingDestinatarios"
              :reduce="option => option.value"
              label="text"
              multiple
              placeholder="Selecione um ou mais representantes"
            />
          </b-form-group>
          <small v-if="hasSubmitted && !recipientSelectionValid()" class="text-danger">
            Não há destinatários válidos selecionados.
          </small>
        </b-col>
      </b-row>
    </validation-observer>
  </b-modal>
</template>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select';
</style>